/**
 * BottomSheet Component
 *
 * Reusable bottom sheet primitive built on vaul.
 * Supports snap points, expandable mode, scrim/drag-dismiss.
 *
 * Requirements: Phase 2 — Native Feel
 */

import * as React from 'react';
import { Drawer } from 'vaul';
import { cn } from '@/lib/utils';

export interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** When true the sheet can grow to near-full height for long content. */
  expandable?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function BottomSheet({
  open,
  onOpenChange,
  title,
  expandable = false,
  children,
  className,
}: BottomSheetProps) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} shouldScaleBackground>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
        <Drawer.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 mt-24 flex flex-col rounded-t-2xl border-t border-outline-variant/20 bg-surface-container-low outline-none shadow-2xl',
            expandable ? 'h-[92vh]' : 'max-h-[88vh]',
            className,
          )}
          aria-describedby={undefined}
        >
          {/* Grab handle */}
          <div
            className="mx-auto mt-3 h-1.5 w-10 flex-shrink-0 rounded-full bg-outline-variant/40"
            aria-hidden="true"
          />

          {/* Header with title + close */}
          <div className="flex items-center justify-between gap-3 px-5 pb-2 pt-3">
            <Drawer.Title className="text-base font-semibold text-on-surface">
              {title ?? ''}
            </Drawer.Title>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="rounded-lg p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface"
            >
              <span className="material-symbols-outlined text-[20px]">close</span>
            </button>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
